import { FC, useEffect, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Heading,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { captureException } from '@sentry/react';
import * as ToastConfigs from '../../configs/toast';
import {
  FileDetailsFileFragment,
  usePiiApproveByIdMutation,
} from '../../graphql/generated';
import { PII_JUSTIFY_TOAST_ID } from './pii-justify';

type Props = {
  isApproving?: boolean;
  onConfirm: (comment: string) => void;
};

export const approveConfirmationMessage =
  'The file will be released and be made available for download.';

export const PiiApprove: FC<Props> = ({ isApproving, onConfirm }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAlertDialog = () => setIsOpen(!isOpen);

  const handlePiiApproval = () => {
    toggleAlertDialog();
  };

  const handleConfirmation = () => {
    onConfirm('Released');
    toggleAlertDialog();
  };

  const renderAlertDialog = (confirmationMessage: string) => {
    return (
      <AlertDialog
        isCentered
        isOpen={isOpen}
        onClose={toggleAlertDialog}
        leastDestructiveRef={undefined}
        data-testid="confirm-approval-dialog"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Release
            </AlertDialogHeader>
            <AlertDialogBody data-testid="confirm-approval-dialog-message">
              {confirmationMessage}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={toggleAlertDialog}
                data-testid="cancel-approval-button"
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme="green"
                onClick={handleConfirmation}
                data-testid="confirm-approval-button"
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    );
  };

  return (
    <Stack
      spacing={6}
      data-testid="pii-approval"
      maxWidth={{ sm: '100%', md: '65%' }}
    >
      <Stack spacing={6}>
        <Box>
          <Heading size="lg" mb="4">
            PII Detected
          </Heading>
          <Alert status="warning">
            <AlertIcon />
            This file does not contain any personally identifiable information
            (PII) and requires you to release the file for download.
          </Alert>
        </Box>
      </Stack>

      <Stack spacing={4}>
        <Box>
          <Button
            colorScheme="green"
            loadingText="Approving"
            isLoading={isApproving}
            onClick={handlePiiApproval}
            data-testid="approve-button"
          >
            Release
          </Button>
        </Box>
      </Stack>
      {renderAlertDialog(approveConfirmationMessage)}
    </Stack>
  );
};

export const PiiApproveContainer: FC<{
  file: FileDetailsFileFragment;
}> = ({ file }) => {
  const toast = useToast();
  const [approvePiiById, { loading }] = usePiiApproveByIdMutation();

  // The PII justify success toast can overlap the approve button, blocking interaction.
  // To prevent this, the toast is dismissed when this component is rendered.
  // This scenario only happens when the user can both justify and approve.
  useEffect(() => {
    if (toast.isActive(PII_JUSTIFY_TOAST_ID)) {
      toast.close(PII_JUSTIFY_TOAST_ID);
    }
  }, [toast]);

  const handleApprove = async (comment: string) => {
    try {
      const result = await approvePiiById({
        variables: {
          cedant: file.cedant,
          input: {
            id: file.id,
            comment,
          },
        },
        refetchQueries: ['GetFile'],
        awaitRefetchQueries: true,
      });

      if (result.data?.piiApproveById) {
        toast({ title: 'Released', status: 'success' });
      }
    } catch (e) {
      toast(ToastConfigs.problemCompletingRequest);
      captureException(e);
    }
  };

  return <PiiApprove isApproving={loading} onConfirm={handleApprove} />;
};